<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { base, central, virtualConfig } from '../api'
import { useNode, type Preset, type Selection } from '../store'
import { alarmText } from '../problems'
import { getItem, setItem } from '../storage'
import { clockAt, dur, dateTime, dot, entityLabel, utcLabel, utcOffsetMin } from '../util'
import Brand from './Brand.vue'

const emit = defineEmits<{
    editNode: []
    media: []
    router: []
    users: []
    changes: []
    tokens: []
    software: []
    configs: []
    templates: []
    alerts: []
    cloud: []
    freeze: []
}>()
const node = useNode()
// the node's clock: its time (the UI's clock follows the node's, node.now)
// in its own zone (the offset its status time is written in), as it draws
// it on the slate; the dot is its NTP sync
const nodeOffset = computed(() => utcOffsetMin(node.status?.time) ?? 0)
// the node's public address, to copy without opening the machine
const publicIP = computed(() => node.status?.machine.public_ip ?? '')
// and its first private one (the rest are on the machine panel)
const privateIPs = computed(() => (node.status?.machine.addresses ?? []).filter((a) => a !== publicIP.value))
const nodeClock = computed(() => (node.status ? clockAt(node.now, nodeOffset.value) : ''))
// the house sync: the DeckLink cards' reference (genlock) inputs, shown
// only on a node with one; locked when every such card is
const reference = computed(() => {
    const cards = (node.status?.machine.decklink_cards ?? []).filter((c) => c.reference)
    if (!cards.length) return null
    const locked = cards.every((c) => c.reference === 'locked')
    const modes = [...new Set(cards.map((c) => c.reference_mode).filter(Boolean))]
    return {
        locked,
        text: locked ? modes.join(' / ') : 'unlocked',
        title: cards
            .map(
                (c) =>
                    `${c.name}: ${c.reference === 'locked' ? `reference locked to ${c.reference_mode ?? 'a signal'}` : 'no reference signal locked'}`,
            )
            .join('\n'),
    }
})
const clockSync = computed(() => {
    const c = node.status?.machine.clock
    if (!c) return { tone: 'bg-off', text: 'sync unknown' }
    if (!c.managed) return { tone: 'bg-off', text: 'kept by the host (a container or WSL)' }
    if (!c.synced) return { tone: 'bg-warn', text: 'not synchronised by NTP' }
    const off =
        c.offset_ms != null
            ? `, ${Math.abs(c.offset_ms) < 1 ? c.offset_ms.toFixed(2) : c.offset_ms.toFixed(0)} ms off`
            : ''
    // a cloud VM's link-local time server (GCP .254, AWS .123), by name
    const server = c.server?.startsWith('169.254.169.') ? "the cloud's own time server" : c.server
    return { tone: 'bg-ok', text: `synchronised by NTP${server ? ' to ' + server : ''}${off}` }
})
const presets: Preset[] = ['10m', '1h', '6h', '24h', '7d']
const showAlarms = ref(false)
const showCustom = ref(false)
const menu = ref(false) // the header's menu (every width)
// a superadmin sees from the menu when the default ffmpeg build is not what
// a build now would make (Node software says why); checked on load and
// whenever the menu opens
const ffOutdated = ref(false)
async function checkFFmpeg() {
    if (!node.fleet || !node.user?.superadmin) return
    try {
        ffOutdated.value = !!(await central.ffmpeg()).outdated
    } catch {
        // central's page shows its own errors
    }
}
watch(() => !!(node.fleet && node.user?.superadmin), checkFFmpeg, { immediate: true })
function toggleMenu() {
    menu.value = !menu.value
    if (menu.value) void checkFFmpeg()
}
function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') menu.value = false
}
onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => window.removeEventListener('keydown', onKey))
const alarms = computed(() => node.openAlarms)
// an alarm's entity, when the node page shows it (a node or central event
// has none to open)
const selectable: readonly string[] = ['input', 'encode', 'destination', 'output', 'machine', 'storage']
const isSelectable = (k: string): k is Selection['kind'] => selectable.includes(k)
function openAlarm(a: (typeof alarms.value)[number]) {
    if (isSelectable(a.kind)) node.select({ kind: a.kind, id: a.entity })
    showAlarms.value = false
}
// grey while the page lost its node (LostBanner): its green said all was well
const overall = computed(() =>
    node.lostAt
        ? 'off'
        : alarms.value.some((a) => a.severity === 'error')
          ? 'bad'
          : alarms.value.length
            ? 'warn'
            : 'ok',
)
// the operator's choice, else the system's (dark in an ops room, light on an iPad in daylight)
const theme = ref(
    getItem('ese.theme') || (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'),
)
function toggleTheme() {
    theme.value = theme.value === 'dark' ? 'light' : 'dark'
    setItem('ese.theme', theme.value)
    document.documentElement.dataset.theme = theme.value
}
document.documentElement.dataset.theme = theme.value

// custom window: a day picker plus optional from/to times
const local = (ms: number) => {
    const d = new Date(ms)
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
    return d.toISOString().slice(0, 16)
}
const from = ref(local(Date.now() - 86400000))
const to = ref(local(Date.now()))
function applyCustom() {
    const f = Date.parse(from.value),
        t = Date.parse(to.value)
    if (!(f < t)) return
    node.setWindow({ from: Math.floor(f / 1000), to: Math.floor(t / 1000) })
    showCustom.value = false
}
function day(offset: number) {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    d.setDate(d.getDate() + offset)
    node.setWindow({ from: Math.floor(d.getTime() / 1000), to: Math.floor(d.getTime() / 1000) + 86400 })
    showCustom.value = false
}
const customLabel = computed(() =>
    'preset' in node.window
        ? 'custom'
        : `${dateTime(new Date(node.window.from * 1000).toISOString())} → ${dateTime(new Date(node.window.to * 1000).toISOString())}`,
)
</script>

<template>
    <header
        class="relative flex min-h-12 flex-none flex-wrap items-center gap-2 border-b border-line chrome bg-bg-1 px-3 py-2"
    >
        <a
            :href="node.central ? '/' : undefined"
            class="mr-1 flex items-center no-underline"
            :aria-label="node.central ? 'BSEP: back to the fleet' : 'BSEP'"
            ><Brand
        /></a>
        <span class="mx-1 h-5 w-px bg-line max-[640px]:hidden" />
        <template v-if="node.fleet">
            <b class="text-sm font-semibold text-fg">Central</b>
            <button class="btn-ghost text-xs" title="Your account and API tokens" @click="emit('tokens')">
                {{ node.user?.email }}
            </button>
        </template>
        <template v-else>
            <a
                v-if="node.central"
                href="/"
                class="text-xs text-fg-2 no-underline hover:text-fg max-[640px]:hidden"
                title="Back to the fleet"
                >Fleet /</a
            >
            <button
                class="inline-flex items-center gap-2 btn-ghost px-1.5 py-0.5 text-sm text-fg"
                title="Node settings"
                @click="emit('editNode')"
            >
                <span :class="dot(overall)" />
                <b class="font-semibold">{{ node.config?.node.name }}</b>
                <span v-if="node.config?.node.site" class="text-fg-2">{{ node.config.node.site }}</span>
            </button>
            <span
                v-if="node.readOnly"
                class="chip"
                title="You are a viewer of this node's groups: you can see and watch it, not change it"
                data-test="view-only"
                >view only</span
            >
            <span
                v-if="node.info?.unsupported"
                class="chip text-warn"
                :title="`This node runs ${node.info.version}, below the oldest version central supports: it is still reached, but nothing is promised`"
                data-test="unsupported-version"
                >unsupported version, update when off air</span
            >
            <span class="chip min-w-[11ch] max-[900px]:hidden" title="Uptime"
                >up {{ dur(node.status?.uptime_seconds ?? 0) }}</span
            >
            <button
                v-if="publicIP"
                class="chip py-0 font-mono hover:bg-bg-3 hover:text-fg max-[900px]:hidden"
                :title="`The node's public address (its own lookup): copy ${publicIP}`"
                data-test="node-public-ip"
                @click="node.copy(publicIP)"
            >
                {{ publicIP }}
            </button>
            <button
                v-if="privateIPs.length"
                class="chip py-0 font-mono text-fg-3 hover:bg-bg-3 hover:text-fg max-[900px]:hidden"
                :title="`The node's private address: copy ${privateIPs[0]}${privateIPs.length > 1 ? ` (also ${privateIPs.slice(1).join(', ')}: the machine panel)` : ''}`"
                data-test="node-private-ip"
                @click="node.copy(privateIPs[0]!)"
            >
                {{ privateIPs[0] }}
            </button>
            <span
                v-if="nodeClock"
                class="chip font-mono max-[640px]:hidden"
                :title="`The node's clock (${utcLabel(nodeOffset)}), ${clockSync.text}`"
                data-test="node-clock"
                ><i class="inline-block size-1.5 rounded-full" :class="clockSync.tone" />{{ nodeClock }}
                <span class="font-sans text-fg-3">{{ utcLabel(nodeOffset) }}</span></span
            >
            <span
                v-if="reference"
                class="chip"
                :class="reference.locked ? 'border-ok/40 text-ok' : 'border-warn text-warn'"
                :title="`Reference (genlock) input\n${reference.title}`"
                data-test="node-reference"
                ><b class="font-semibold">REF</b> {{ reference.text }}</span
            >
        </template>
        <span class="min-w-0 flex-1" />
        <!-- on a phone the window and the alarms go to the second line: the menu stays by the name -->
        <div
            class="flex h-7 max-w-full overflow-hidden rounded-md border border-line-strong bg-bg max-[640px]:order-1"
            role="radiogroup"
            aria-label="Time window"
        >
            <button
                v-for="r in presets"
                :key="r"
                class="rounded-none btn-ghost px-2.5 py-0 text-xs max-[640px]:px-1.5"
                :class="{ 'bg-bg-3 font-medium text-fg': 'preset' in node.window && node.window.preset === r }"
                @click="node.setWindow({ preset: r })"
            >
                {{ r }}
            </button>
            <button
                class="max-w-[42vw] truncate rounded-none btn-ghost px-2.5 py-0 text-xs max-[640px]:px-1.5"
                :class="{ 'bg-bg-3 font-medium text-fg': !node.live }"
                :title="customLabel"
                @click="showCustom = !showCustom"
            >
                {{ customLabel }}
            </button>
        </div>
        <!-- one button the same size whatever the count: the count changes, the header does not move -->
        <button
            v-if="!node.fleet"
            class="inline-flex h-7 min-w-32 items-center justify-center gap-1.5 py-0 text-xs max-[640px]:order-1"
            :class="
                alarms.length
                    ? overall === 'bad'
                        ? 'border-bad/60 text-bad'
                        : 'border-warn/60 text-warn'
                    : 'text-fg-2'
            "
            :aria-expanded="showAlarms"
            @click="showAlarms = !showAlarms"
        >
            <span :class="dot(overall)" />
            {{
                alarms.length
                    ? `${alarms.length} open alarm${alarms.length > 1 ? 's' : ''}`
                    : node.lostAt
                      ? 'alarms not known'
                      : 'no alarms'
            }}
        </button>
        <!-- what is needed now and then sits behind one menu at every width
             (Niels: the header showed too much); the window and the alarms stay -->
        <button
            class="inline-flex h-7 items-center gap-1.5 py-0 text-xs"
            :aria-expanded="menu"
            aria-controls="header-menu"
            aria-haspopup="menu"
            @click="toggleMenu"
        >
            <svg viewBox="0 0 16 16" class="size-3.5" aria-hidden="true">
                <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" /></svg
            >Menu<span
                v-if="ffOutdated"
                class="text-warn"
                aria-hidden="true"
                title="the default ffmpeg build is outdated"
            >
                ●</span
            >
        </button>
        <div v-if="menu" class="fixed inset-0 z-14" @click="menu = false" />
        <nav
            v-if="menu"
            id="header-menu"
            aria-label="Menu"
            class="absolute top-[calc(100%+4px)] right-3 z-15 flex max-h-[80vh] min-w-56 popover flex-col overflow-auto p-1.5 *:flex *:items-center *:justify-start *:gap-1.5 *:text-left"
            @click="menu = false"
        >
            <!-- central's own pages: the monitor wall, any source across the
                 fleet large, the broadcaster directory. Links, styled as the
                 menu's buttons (a link has none of a button's padding, border
                 or colour: they stood out) -->
            <template v-if="node.central">
                <a href="/wall" class="rounded-md border btn-ghost px-2.5 py-1.25 no-underline" data-test="menu-wall"
                    >Wall</a
                >
                <a href="/watch" class="rounded-md border btn-ghost px-2.5 py-1.25 no-underline" data-test="menu-watch"
                    >Watch</a
                >
                <a
                    href="/broadcasters"
                    class="rounded-md border btn-ghost px-2.5 py-1.25 no-underline"
                    data-test="menu-broadcasters"
                    >Broadcasters</a
                >
                <div class="my-1 border-t border-line" role="separator" />
            </template>
            <template v-if="node.fleet">
                <button class="btn-ghost" @click="emit('configs')">Configs</button>
                <button class="btn-ghost" @click="emit('templates')">Encoding templates</button>
                <button v-if="node.user?.superadmin" class="btn-ghost" @click="emit('software')">
                    Node software<span v-if="ffOutdated" class="text-warn" data-testid="menu-ffmpeg-outdated">
                        · rebuild ffmpeg</span
                    >
                </button>
                <button class="btn-ghost" @click="emit('media')">Media</button>
                <button class="btn-ghost" data-test="menu-changes" @click="emit('changes')">Changes</button>
                <div class="my-1 border-t border-line" role="separator" />
                <button v-if="node.user?.superadmin" class="btn-ghost" @click="emit('users')">Users</button>
                <button v-if="node.user?.superadmin" class="btn-ghost" @click="emit('alerts')">Alerts</button>
                <button v-if="node.user?.superadmin" class="btn-ghost" data-test="menu-freeze" @click="emit('freeze')">
                    Deploy freeze
                </button>
                <button v-if="node.user?.superadmin" class="btn-ghost" data-test="menu-cloud" @click="emit('cloud')">
                    Cloud nodes
                </button>
                <button class="btn-ghost" @click="emit('tokens')">Your account and API tokens</button>
                <!-- this central's version of the Companion module (Stream
                     Deck): the image builds it, /install/companion.tgz -->
                <a
                    href="/install/companion.tgz"
                    download
                    class="rounded-md border btn-ghost px-2.5 py-1.25 no-underline"
                    data-test="menu-companion"
                    title="The Bitfocus Companion module (Stream Deck) of this central's version. In Companion: Modules, Import module package; then a connection with central's address, a Companion API token (Your account and API tokens) and the node's name"
                    >Companion module (download)</a
                >
            </template>
            <template v-else>
                <button v-if="!node.readOnly" class="btn-ghost" @click="emit('router')">Router</button>
                <button class="btn-ghost" @click="emit('media')">Media</button>
                <button
                    v-if="virtualConfig === null && (!node.central || node.user?.superadmin)"
                    class="btn-ghost"
                    @click="emit('software')"
                >
                    Software
                </button>
                <div class="my-1 border-t border-line" role="separator" />
                <a
                    v-if="virtualConfig === null"
                    class="rounded-md border btn-ghost px-2.5 py-1.25 no-underline"
                    :href="base + '/api/support'"
                    download
                    title="Everything about this node in one file (state, findings, log, events, config without its secrets) to send to support or an AI assistant"
                    >Support log</a
                >
            </template>
            <a
                class="rounded-md border btn-ghost px-2.5 py-1.25 no-underline"
                :href="
                    base.startsWith('/nodes/v')
                        ? '/api-docs.html'
                        : '/api-docs.html' + (base ? '?node=' + base.slice(7) : '')
                "
                target="_blank"
                title="The API, browsable and to try out (Swagger UI)"
                >API docs</a
            >
            <div class="my-1 border-t border-line" role="separator" />
            <button v-if="!node.fleet" class="btn-ghost" @click="node.togglePanel()">
                {{ node.panelOpen ? 'Hide the detail panel' : 'Show the detail panel' }}
            </button>
            <button class="btn-ghost" @click="toggleTheme">
                {{ theme === 'dark' ? 'Light theme' : 'Dark theme' }}
            </button>
            <div class="my-1 border-t border-line" role="separator" />
            <button class="btn-ghost" @click="node.logout()">Sign out</button>
        </nav>

        <div
            v-if="showCustom"
            class="absolute top-[calc(100%+4px)] right-50 z-15 w-105 popover p-3 max-[640px]:inset-x-3 max-[640px]:w-auto"
        >
            <div class="mb-2 flex items-center gap-2">
                <button @click="day(0)">Today</button><button @click="day(-1)">Yesterday</button
                ><button @click="day(-2)">2 days ago</button>
            </div>
            <div class="grid grid-cols-2 gap-2.5 max-[640px]:grid-cols-1">
                <div class="mb-2.5"><label>From</label><input v-model="from" type="datetime-local" /></div>
                <div class="mb-2.5"><label>To</label><input v-model="to" type="datetime-local" /></div>
            </div>
            <div class="flex items-center justify-end gap-2">
                <button @click="showCustom = false">Cancel</button
                ><button class="btn-primary" @click="applyCustom">Show</button>
            </div>
            <p class="mt-2 mb-0 text-xs text-fg-3">
                This node keeps about 30 days of events locally. Older history lives in central.
            </p>
        </div>
        <div
            v-if="showAlarms && alarms.length"
            class="absolute top-[calc(100%+4px)] right-3 z-15 max-h-[60vh] max-w-175 min-w-105 popover overflow-auto max-[640px]:left-3 max-[640px]:min-w-0"
        >
            <div
                v-for="a in alarms"
                :key="a.id"
                class="grid cursor-pointer grid-cols-[8px_150px_1fr] items-center gap-2.5 border-b border-line px-3 py-1.75 text-xs hover:bg-bg-2 max-[640px]:grid-cols-[8px_1fr]"
                role="button"
                tabindex="0"
                @click="openAlarm(a)"
                @keydown.enter.prevent="openAlarm(a)"
                @keydown.space.prevent="openAlarm(a)"
            >
                <span :class="dot(a.severity === 'error' ? 'bad' : 'warn')" />
                <span class="font-mono text-xs text-fg-2 tabular-nums">{{ dateTime(a.ts) }}</span>
                <span
                    ><b>{{ entityLabel(node.config, a.kind, a.entity) }}</b> ·
                    {{ alarmText(a, node.status?.machine) }}</span
                >
            </div>
        </div>
    </header>
</template>
